/**
 * Zustand store for Groups Management - UI STATE ONLY
 *
 * This store manages purely client-side UI state:
 * - Collapse state (which groups are expanded/collapsed)
 * - Drag state (active drag, collapsed for drag)
 * - Highlight state (recently moved groups)
 *
 * Server data lives in React Query. Derived data (maps, sorted groups)
 * is computed in hooks via useMemo.
 */

import { create } from "zustand";

/**
 * @typedef {Object} GroupsUIState
 * @property {Set<number>} collapsedGroups - Set of collapsed group IDs
 * @property {number|null} activeId - ID of group currently being dragged
 * @property {boolean} hasCollapsedForDrag - Whether siblings were collapsed for drag
 * @property {number|null} lastMovedGroupId - ID of last moved group (for highlight)
 */
export const useGroupsStore = create((set, get) => ({
    // ============================================
    // COLLAPSE STATE
    // ============================================
    collapsedGroups: new Set(),

    toggleCollapse: (groupId) => {
        set((state) => {
            const newSet = new Set(state.collapsedGroups);
            if (newSet.has(groupId)) {
                newSet.delete(groupId);
            } else {
                newSet.add(groupId);
            }
            return { collapsedGroups: newSet };
        });
    },

    expandAll: () => {
        set({ collapsedGroups: new Set() });
    },

    /**
     * Collapse all groups that have children
     * @param {Array} groups - All groups with hasChildren field (from server)
     */
    collapseAll: (groups) => {
        const allCollapsible = groups
            .filter((g) => g.hasChildren)
            .map((g) => g.ID);
        set({ collapsedGroups: new Set(allCollapsible) });
    },

    /**
     * Collapse siblings of a group (for drag operations)
     * @param {number} activeId - ID of the group being dragged
     * @param {Array} groups - All groups with hasChildren/section fields (from server)
     */
    collapseSiblings: (activeId, groups) => {
        const draggedGroup = groups.find((g) => g.ID === activeId);
        if (!draggedGroup) return;

        const { collapsedGroups } = get();
        const newCollapsed = new Set(collapsedGroups);

        // Find all siblings (groups at the same level with same parent)
        const siblings = groups.filter((g) => {
            if (draggedGroup.parentGroupID === 0 && g.parentGroupID === 0) {
                // Top-level: must be same section type (orphanedParents vs grandparents)
                return g.section === draggedGroup.section;
            }
            // Non-top-level: must have same parent
            return g.parentGroupID === draggedGroup.parentGroupID;
        });

        // Collapse all siblings that have children
        siblings.forEach((sibling) => {
            if (sibling.hasChildren) {
                newCollapsed.add(sibling.ID);
            }
        });

        set({ collapsedGroups: newCollapsed });
    },

    // ============================================
    // DRAG STATE
    // ============================================
    activeId: null,
    hasCollapsedForDrag: false,
    /**
     * PERFORMANCE: Pre-computed drag validity data
     * Set once when drag starts, cleared when drag ends.
     * Allows O(1) validity lookups instead of O(n) per-item computation.
     */
    validTargetIds: null, // Set<number> | null
    draggedParentId: null, // number | null

    setActiveId: (id) => {
        set({ activeId: id });
    },

    /**
     * Set pre-computed drag validity data
     * @param {Set<number>|null} validIds - Set of valid target IDs, or null to clear
     * @param {number|null} parentId - Parent ID of dragged item, or null to clear
     */
    setDragValidity: (validIds, parentId) => {
        set({ validTargetIds: validIds, draggedParentId: parentId });
    },

    setHasCollapsedForDrag: (value) => {
        set({ hasCollapsedForDrag: value });
    },

    // ============================================
    // HIGHLIGHT STATE
    // ============================================
    lastMovedGroupId: null,

    setLastMovedGroupId: (groupId) => {
        set({ lastMovedGroupId: groupId });
        // Auto-clear after 3 seconds
        if (groupId !== null) {
            setTimeout(() => {
                if (get().lastMovedGroupId === groupId) {
                    set({ lastMovedGroupId: null });
                }
            }, 3000);
        }
    },

    // ============================================
    // RESET (for tests or cleanup)
    // ============================================
    reset: () => {
        set({
            collapsedGroups: new Set(),
            activeId: null,
            hasCollapsedForDrag: false,
            validTargetIds: null,
            draggedParentId: null,
            lastMovedGroupId: null,
        });
    },
}));
